import Vapi from '@vapi-ai/web';
import WaveSurfer from 'wavesurfer.js';
import * as faceapi from 'face-api.js';
import * as Sentry from "@sentry/browser";
import * as JsCookie from "js-cookie"; // generated
// pass-through for Cookies API
export const Cookies = JsCookie.default;

class ExtendedVapi extends Vapi {

  constructor(apiToken, callSetup, apiBaseUrl) {
    super(apiToken, apiBaseUrl);
    this.microphoneEnabled = false;
    this.isScreenSharing = false;
    this.cameraStarted = false; 
    this.callSetup = callSetup;

    this.on('call-start', () => {
      this.checkMicrophoneStatus();
      this.setupScreenShareListener();
    });

    
    // Register for device changes
      this.callSetup.onDeviceChange(() => this.updateInputDevices());
  }

  async roomDetails() {
    return this.call.room();
  }

  async sessionDetails() {
    if (!this.call) {
      throw new Error('Call has not started yet');
    }

    try {
      const roomInfo = await this.call.room();
      const participants = this.call.participants();

      // Format participant details
      const participantDetails = Object.values(participants).map(p => ({
        session_id: p.session_id,
        user_name: p.user_name,
        user_id: p.user_id,
        isLocal: p.local
      }));

      return {
        call: {
          id: roomInfo.id,
          name: roomInfo.name,
          url: roomInfo.url,
        },
        participants: participantDetails,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting session details:', error);
      throw new Error('Failed to get session details');
    }
  }

  checkMicrophoneStatus() {
    if (this.isMuted()) {
      console.log('Microphone is muted, alerting user');
      this.microphoneEnabled = false;
      this.alertMicrophoneDisabled();
    } else {
      this.microphoneEnabled = true;
    }
  }

  alertMicrophoneDisabled() {
    console.warn('Microphone is not enabled. Please enable your microphone to participate in the call.');
    alert('Please enable your microphone to participate in the call.');
  }

  setMuted(mute) {
    super.setMuted(mute);
    this.checkMicrophoneStatus();
  }

  isMicrophoneEnabled() {
    return this.microphoneEnabled;
  }

  setupScreenShareListener() {
    this.call.on('participant-updated', (event) => {
      const localParticipant = this.call.participants().local;
      const isScreenSharing = localParticipant.tracks.screenVideo?.state === 'playable';
  
      if (isScreenSharing !== this.isScreenSharing) {
        this.isScreenSharing = isScreenSharing;
        console.log(`Screen sharing is ${isScreenSharing ? 'active' : 'inactive'}`);
        this.emit('screen-share-active', isScreenSharing);
      }
    });
  }

  async _startScreenShare() {
    if (!this.call) {
      throw new Error('Call has not started yet');
    }
  
    try {
      await this.call.startScreenShare();
    } catch (error) {
      console.error('Error initiating screen share:', error);
      this.emit('screen-share-active', false);
      throw error;
    }
  }

  async stopScreenShare() {
    if (!this.call) {
      throw new Error('Call has not started yet');
    }
    try {
      await this.call.stopScreenShare();
      console.log('Screen share stopped');
    } catch (error) {
      console.error('Error stopping screen share:', error);
      throw error;
    }
  }

  getParticipants() {
    if (!this.call) {
      throw new Error('Call has not started yet');
    }
    return this.call.participants();
  }

  async startCamera() {
    if (!this.call) {
      throw new Error('Call object is not initialized.');
    }

    if (this.cameraStarted) {
      console.warn('Camera is already started.');
      return;
    }

    try {
      const cameraInfo = await this.call.setLocalVideo(true);
      this.cameraStarted = true;

      const localParticipant = this.call.participants().local;
      const audioTrack = localParticipant.tracks.audio.track;
      const videoTrack = localParticipant.tracks.video.track;
      
      const stream = new MediaStream([videoTrack]);

      // update the stream to use the one daily is using
      this.callSetup.gotStream(stream);

      // Emit an event with the local tracks
      this.emit('camera-started', { audioTrack, videoTrack, cameraInfo });
    } catch (error) {
      console.error('Failed to start camera:', error);
      this.emit('error', error);
    }
  }

  // setCallSetup(callSetup) {
  //   this.callSetup = callSetup;
  //   // Add listeners for device changes
  //   this.callSetup.audioInputSelect.addEventListener('change', () => this.updateInputDevices());
  //   this.callSetup.videoInputSelect.addEventListener('change', () => this.updateInputDevices());
  // }

  async updateInputDevices() {
    if (!this.call) {
      console.warn('Call has not started yet, cannot update input devices');
      return;
    }

    const audioDeviceId = this.callSetup.audioInputSelect.value;
    const videoDeviceId = this.callSetup.videoInputSelect.value;

    try {
      const result = await this.call.setInputDevicesAsync({
        audioDeviceId: audioDeviceId,
        videoDeviceId: this.callSetup.selectedMedia === 'vid' ? videoDeviceId : null
      });
      console.log('Input devices updated:', result);
    } catch (error) {
      console.error('Error updating input devices:', error);
      this.emit('error', new Error('Failed to update input devices'));
    }
  }

  async listInputDevices() {
    if (!this.call) {
      console.warn('Call has not started yet, cannot list input devices');
      return null;
    }

    try {
      const devices = await this.call.getInputDevices();
      return devices;
    } catch (error) {
      console.error('Error listing input devices:', error);
      this.emit('error', new Error('Failed to list input devices'));
      return null;
    }
  }


  // Override the start method to set up CallSetup
  async start(...args) {
    const result = await super.start(...args);

    // First set the correct input devices
    if (this.callSetup) {
      await this.updateInputDevices();
    }

    // Then start the camera with the selected devices
    if (this.call && this.callSetup.selectedMedia === 'vid') {
      await this.startCamera();
    }

    return result;
  }

}



class CallSetup {
  constructor(initialMediaType) {
      this.videoElement = document.getElementById('webcam-container');
      this.audioInputSelect = document.getElementById('audio-input-select');
      this.videoInputSelect = document.getElementById('video-input-select');
      this.mediaSelectors = document.querySelectorAll('.interview-mode-selector');

      
      this.selectors = [this.audioInputSelect, this.videoInputSelect];
      
      this.selectedMedia = initialMediaType || 'audio';
      this.faceDetected = false;
      this.faceDetectionInterval = null;
      this.stream = null;

      // New callbacks
      this.onFaceDetectionUpdate = null;
      // this.onStatusUpdate = null;
      
      this.deviceChangeCallback = null; // Initialize callback
      
      this.init();
  }

  async init() {
    try {
      this.toggleUIElements();
      if (this.selectedMedia === 'vid') {
        await this.loadFaceDetectionModels();
      }
      this.attachEventListeners();
      await this.start();

    } catch (error) {
      this.handleError(error);
    }
  }

  async loadFaceDetectionModels() {
      await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/static/assets/models'),
      ]);
      console.log('Face detection models loaded successfully');
  }

  handleMediaPermissionError(error) {
    console.error('Media permission error:', error);
  
    let errorMessage = "We couldn't access your ";
    let instructions = "To participate in the interview, please enable access to your ";
  
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      if (this.selectedMedia === 'vid') {
        errorMessage += "camera or microphone. ";
        instructions += "camera and microphone. You can do this by clicking the camera icon in your browser's address bar and selecting 'Allow'.";
      } else {
        errorMessage += "microphone. ";
        instructions += "microphone. You can do this by clicking the microphone icon in your browser's address bar and selecting 'Allow'.";
      }
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      if (this.selectedMedia === 'vid') {
        errorMessage += "camera or microphone. ";
        instructions += "camera and microphone. Please make sure your devices are properly connected and try again.";
      } else {
        errorMessage += "microphone. ";
        instructions += "microphone. Please make sure your microphone is properly connected and try again.";
      }
    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      if (this.selectedMedia === 'vid') {
        errorMessage += "camera or microphone. They may be already in use by another application. ";
        instructions += "camera and microphone. Please close other applications that might be using them and try again.";
      } else {
        errorMessage += "microphone. It may be already in use by another application. ";
        instructions += "microphone. Please close other applications that might be using it and try again.";
      }
    }
  
    this.showAdminMessage(errorMessage + instructions);
  }

  attachEventListeners() {
      this.audioInputSelect.onchange = () => {
          this.start();
          // this.updateStartButtonState();
          
          // Notify ExtendedVapi about audio device change
          this.deviceChangeCallback?.('audio');
      }

      this.videoInputSelect.onchange = () => {
          this.start();
          this.faceDetected = false;
          // this.updateStartButtonState();

          // Notify ExtendedVapi about video device change
          this.deviceChangeCallback?.('video');
      }

      // Get all interview mode selectors
      this.mediaSelectors = document.querySelectorAll('.interview-mode-selector');

      // Add event listener to each selector
      this.mediaSelectors.forEach(selector => {
          selector.addEventListener("change", (e) => {
              const isVideo = e.target.checked;
              this.selectedMedia = isVideo ? 'vid' : 'aud';
              
              // Sync all selectors
              this.mediaSelectors.forEach(sel => {
                sel.checked = isVideo;  
              });

              console.log('Media mode changed to:', this.selectedMedia);
              this.toggleUIElements();
              this.start();
          });
      });
  }

  // Method to register callback
  onDeviceChange(callback) {
    this.deviceChangeCallback = callback;
  }

  async getDevices() {
    const deviceInfos = await navigator.mediaDevices.enumerateDevices();
    const values = this.selectors.map(select => select.value);
    this.selectors.forEach(select => {
        while (select.firstChild) {
            select.removeChild(select.firstChild);
        }
    });

    for (let i = 0; i !== deviceInfos.length; ++i) {
        const deviceInfo = deviceInfos[i];
        const option = document.createElement('option');
        option.value = deviceInfo.deviceId;
        if (deviceInfo.kind === 'audioinput') {
            option.text = deviceInfo.label || `microphone ${this.audioInputSelect.length + 1}`;
            this.audioInputSelect.appendChild(option);
        } else if (deviceInfo.kind === 'videoinput') {
            option.text = deviceInfo.label || `camera ${this.videoInputSelect.length + 1}`;
            this.videoInputSelect.appendChild(option);
        }
    }

    this.selectors.forEach((select, selectorIndex) => {
        if (Array.prototype.slice.call(select.childNodes).some(n => n.value === values[selectorIndex])) {
            select.value = values[selectorIndex];
        }
    });
  }

  async start() {
      const audioSource = this.audioInputSelect.value;
      const videoSource = this.videoInputSelect.value;
      const constraints = {
          audio: {deviceId: audioSource ? {exact: audioSource} : undefined},
          video: this.selectedMedia === 'vid' ? {
              deviceId: videoSource ? {exact: videoSource} : undefined,
              facingMode: "user",
              width: 640,
              height: 480,
              bitrate: 2500000,
              frameRate: 30
          } : false
      };
      try {
          this.stream = await navigator.mediaDevices.getUserMedia(constraints);
          this.gotStream(this.stream);
          await this.getDevices();
          
          if (this.selectedMedia === 'vid') {
              if (!this.faceDetectionModelsLoaded) {
                  await this.loadFaceDetectionModels();
              }
              this.startFaceDetection();
          } else {
              if (this.faceDetectionInterval) {
                  clearInterval(this.faceDetectionInterval);
              }
              this.faceDetected = false;
              // this.updateStartButtonState();
          }
          return Promise.resolve();
      } catch (error) {
          this.handleError(error);
          return Promise.reject(error);
      }
  }

  gotStream(stream) {
      this.stream = stream;
      this.videoElement.srcObject = stream;
      this.videoElement.muted = true;
  }

  startFaceDetection() {
      const detectFace = async () => {
          try {
              const detections = await faceapi.detectAllFaces(this.videoElement, new faceapi.TinyFaceDetectorOptions({ inputSize: 128 }));
              console.log('Face detections:', detections);
              
              const previousFaceDetected = this.faceDetected;
              this.faceDetected = detections.length > 0;
              
              if (this.faceDetected !== previousFaceDetected) {
                  // this.updateStartButtonState();
                  if (this.onFaceDetectionUpdate) {
                      this.onFaceDetectionUpdate(this.faceDetected);
                  }
              }
              
              if (this.faceDetected && this.faceDetectionInterval) {
                  clearInterval(this.faceDetectionInterval);
                  console.log('Face detection interval cleared');
              }
          } catch (error) {
              console.error('Error detecting faces:', error);
              this.faceDetected = false;
          }
      };

      if (this.faceDetectionInterval) {
          clearInterval(this.faceDetectionInterval);
      }
      this.faceDetectionInterval = setInterval(detectFace, 2000); // 2 seconds
  }

  // updateStartButtonState() {
  //   console.log('in updateStartButtonState')
  //     const isReady = true; //this.selectedMedia === 'audio' || this.faceDetected;
  //     if (this.onStatusUpdate) {
  //         this.onStatusUpdate({ ready: isReady });
  //     }
  // }

  toggleUIElements() {
      document.getElementById('vid-recorder').style.display = this.selectedMedia === 'vid' ? 'block' : 'none';
      document.getElementById('aud-recorder').style.display = this.selectedMedia === 'audio' ? 'block' : 'none';
      this.videoInputSelect.style.display = this.selectedMedia === 'vid' ? 'block' : 'none';
      this.videoElement.style.display = this.selectedMedia === 'vid' ? 'block' : 'none';
      this.videoElement.srcObject = this.stream;
  }

  handleError(error) {
      console.error('Error: ', error);
      this.showAdminMessage(`Error: ${error.message}`);
  }

  showAdminMessage(message) {
      var messages = document.getElementById('messages');
      if (!messages.innerHTML.includes(message)) {
          var div = document.createElement('div');
          div.classList.add('server-message', 'py-4');
          var p = document.createElement('p');
          p.innerHTML = "<strong>ADMIN: </strong>" + message;
          div.append(p);
          messages.append(div);
      }
  }
}

// waveform animation for volume changes
class WaveformAnimation {
  constructor(canvasId) {
      this.canvas = document.getElementById(canvasId);
      this.ctx = this.canvas.getContext('2d');
      this.volume = 0;
      this.animationFrameId = null;

      this.resizeCanvas();
      window.addEventListener('resize', () => this.resizeCanvas());

      this.draw = this.draw.bind(this);
      // this.simulateVolumeChanges();
      this.draw();
  }

  resizeCanvas() {
      this.canvas.width = this.canvas.offsetWidth;
      this.canvas.height = this.canvas.offsetHeight;
  }

  draw() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      
      const centerY = this.canvas.height / 2;
      const amplitude = this.volume * 50; // Adjust this multiplier to change sensitivity

      this.ctx.beginPath();
      this.ctx.moveTo(0, centerY);

      for (let x = 0; x < this.canvas.width; x++) {
          const y = centerY + Math.sin(x * 0.05) * amplitude;
          this.ctx.lineTo(x, y);
      }

      this.ctx.strokeStyle = 'blue';
      this.ctx.lineWidth = 2;
      this.ctx.stroke();

      this.animationFrameId = requestAnimationFrame(this.draw);
  }

  setVolume(newVolume) {
      this.volume = newVolume;
  }

  stop() {
      cancelAnimationFrame(this.animationFrameId);
  }
}


// Make classes available globally
window.ExtendedVapi = ExtendedVapi;
window.CallSetup = CallSetup;
window.WaveformAnimation = WaveformAnimation;
window.WaveSurfer = WaveSurfer;

// Optionally, you can also expose the original Vapi if needed
window.Vapi = Vapi;

// Make faceapi available globally
window.faceapi = faceapi;


// Function to check if we're on localhost
function isLocalhost() {
  return window.location.hostname === "localhost" || 
         window.location.hostname === "127.0.0.1" ||
         window.location.hostname === "";
}

// Only initialize Sentry if we're not on localhost
if (!isLocalhost()) {
  Sentry.init({
    dsn: "https://510b6dd9b962c938bb7c1a7ba0bf79d4@o4506203840053248.ingest.us.sentry.io/4507887716335616",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      /^https?:\/\/(www\.|mbblabs\.)?rightjoin\.co/,
      /^https?:\/\/careers\.lawyerpanel\.org/,
      /^https?:\/\/careers\.expertpanel\.org/,
    ],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0, 
  });

  window.Sentry = Sentry;
} else {
  console.log("Sentry disabled on localhost");
  // Provide a mock Sentry object to prevent errors if Sentry is used elsewhere in the code
  window.Sentry = {
    captureException: () => {},
    captureMessage: () => {},
    // Add other commonly used Sentry methods as needed
  };
}



async function logInputDevices(data) {
  fetch('save-input-devices/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': Cookies.get('csrftoken'),
    },
    body: JSON.stringify(data),
    credentials: 'same-origin'
  })
  .then(response => response.json())
  .then(data => {
    console.log('Input devices saved:', data);
  })
  .catch((error) => {
    console.error('Error saving input devices:', error);
  });
}
window.logInputDevices = logInputDevices;